import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createRouteInputSchema, type CreateRouteInput, type Route } from 'fuel-benchmark-contracts';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { clearableTextFieldOptions, numberFieldOptions, serverMessage, submitErrorKey, textFieldOptions } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { ROUTE_TYPES } from './condition-display';

export interface RouteSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Absent for a create; present for a rename, which only PATCHes name and description. */
  route?: Route;
}

const EMPTY_VALUES = { code: '', name: '', routeType: 'URBAN' } as Partial<CreateRouteInput>;

/**
 * Creating a route and renaming one share this panel. Distance and the elevation survey are read-only on an
 * edit because a measurable change is a new version, never an edit (plan §4).
 *
 * The vertical survey is two paired fields rather than four loose ones: a total climb with the distance it is
 * spread over, and the same again for the descent. That is how the numbers are actually surveyed, and it is
 * what keeps a round trip from being entered as a net of zero.
 */
export const RouteSheet = ({ isOpen, onClose, route }: RouteSheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEdit = route !== undefined;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateRouteInput>({
    resolver: zodResolver(createRouteInputSchema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSubmitError(null);
    reset(
      route
        ? {
            code: route.code,
            name: route.name,
            routeType: route.routeType,
            distanceKm: route.distanceKm ?? undefined,
            elevationGainM: route.elevationGainM ?? undefined,
            ascentDistanceKm: route.ascentDistanceKm ?? undefined,
            elevationLossM: route.elevationLossM ?? undefined,
            descentDistanceKm: route.descentDistanceKm ?? undefined,
            description: route.description ?? undefined,
          }
        : EMPTY_VALUES,
    );
  }, [isOpen, route, reset]);

  const createRoute = useMutation(orpc.routes.create.mutationOptions());
  const updateRoute = useMutation(orpc.routes.update.mutationOptions());

  const routeTypeOptions: SelectOption[] = ROUTE_TYPES.map((value) => ({ value, label: t(`enums.routeType.${value}`) }));

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    try {
      if (isEdit) {
        await updateRoute.mutateAsync({ id: route.id, name: values.name, description: values.description ?? null });
      } else {
        await createRoute.mutateAsync(values);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.routes.list.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
      ]);
      onClose();
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  });

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('conditions.routes.form.editTitle') : t('conditions.routes.form.createTitle')}
      description={isEdit ? t('conditions.routes.form.editSubtitle') : t('conditions.routes.form.createSubtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="route-form"
            isLoading={isSubmitting}
          >
            {isSubmitting ? t('actions.saving') : t('actions.save')}
          </Button>
        </>
      }
    >
      <form
        id="route-form"
        noValidate
        onSubmit={onSubmit}
        className="space-y-4"
      >
        <FormField
          id="route-code"
          label={t('conditions.routes.form.code')}
          error={errors.code?.message}
        >
          <Input
            id="route-code"
            readOnly={isEdit}
            disabled={isEdit}
            placeholder={t('conditions.routes.form.codePlaceholder')}
            className="font-mono"
            hasError={Boolean(errors.code)}
            {...register('code')}
          />
        </FormField>

        <FormField
          id="route-name"
          label={t('conditions.routes.form.name')}
          error={errors.name?.message}
        >
          <Input
            id="route-name"
            placeholder={t('conditions.routes.form.namePlaceholder')}
            hasError={Boolean(errors.name)}
            {...register('name')}
          />
        </FormField>

        <FormField
          id="route-type"
          label={t('conditions.routes.form.routeType')}
          error={errors.routeType?.message}
        >
          <Select
            id="route-type"
            options={routeTypeOptions}
            disabled={isEdit}
            hasError={Boolean(errors.routeType)}
            {...register('routeType')}
          />
        </FormField>

        <FormField
          id="distanceKm"
          label={t('conditions.routes.form.distanceKm')}
          hint={isEdit ? undefined : t('conditions.routes.form.distanceHint')}
          error={errors.distanceKm?.message}
        >
          <Input
            id="distanceKm"
            type="number"
            step="0.01"
            inputMode="decimal"
            readOnly={isEdit}
            disabled={isEdit}
            hasError={Boolean(errors.distanceKm)}
            {...register('distanceKm', numberFieldOptions)}
          />
        </FormField>

        {/*
          Ascent and descent are two independent surveys, each a total paired with the distance it is spread
          over — never one signed number. Grouping them that way is what makes the hint below true of the form
          rather than a caption bolted onto it.
        */}
        <fieldset className="space-y-3 rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-medium text-foreground">{t('conditions.routes.form.ascentLegend')}</legend>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              id="elevationGainM"
              label={t('conditions.routes.form.elevationGainM')}
              error={errors.elevationGainM?.message}
            >
              <Input
                id="elevationGainM"
                type="number"
                inputMode="numeric"
                readOnly={isEdit}
                disabled={isEdit}
                hasError={Boolean(errors.elevationGainM)}
                {...register('elevationGainM', numberFieldOptions)}
              />
            </FormField>

            <FormField
              id="ascentDistanceKm"
              label={t('conditions.routes.form.ascentDistanceKm')}
              error={errors.ascentDistanceKm?.message}
            >
              <Input
                id="ascentDistanceKm"
                type="number"
                step="0.01"
                inputMode="decimal"
                readOnly={isEdit}
                disabled={isEdit}
                hasError={Boolean(errors.ascentDistanceKm)}
                {...register('ascentDistanceKm', numberFieldOptions)}
              />
            </FormField>
          </div>
        </fieldset>

        <fieldset className="space-y-3 rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-medium text-foreground">{t('conditions.routes.form.descentLegend')}</legend>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              id="elevationLossM"
              label={t('conditions.routes.form.elevationLossM')}
              error={errors.elevationLossM?.message}
            >
              <Input
                id="elevationLossM"
                type="number"
                inputMode="numeric"
                readOnly={isEdit}
                disabled={isEdit}
                hasError={Boolean(errors.elevationLossM)}
                {...register('elevationLossM', numberFieldOptions)}
              />
            </FormField>

            <FormField
              id="descentDistanceKm"
              label={t('conditions.routes.form.descentDistanceKm')}
              error={errors.descentDistanceKm?.message}
            >
              <Input
                id="descentDistanceKm"
                type="number"
                step="0.01"
                inputMode="decimal"
                readOnly={isEdit}
                disabled={isEdit}
                hasError={Boolean(errors.descentDistanceKm)}
                {...register('descentDistanceKm', numberFieldOptions)}
              />
            </FormField>
          </div>
        </fieldset>

        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {t('conditions.routes.form.elevationHint')}
        </p>

        <FormField
          id="route-description"
          label={t('conditions.routes.form.description')}
          error={errors.description?.message}
        >
          <textarea
            id="route-description"
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            {...register('description', isEdit ? clearableTextFieldOptions : textFieldOptions)}
          />
        </FormField>

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </form>
    </Sheet>
  );
};
